import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { AddIcon, RemoveIcon } from '@/icons';
import { Profit, Loss } from './profit-loss';
import { formatAmountDisplay } from './utils';

type TpSlPanelProps = {
    isOpen: boolean;
    step: number;
    adjustPrice: (current?: number, delta?: number) => string;
    errorTP: string;
    errorSl: string;
    handleClear: (type: 'tp' | 'sl') => void;
    stopLossPrice?: number;
    takeProfitPrice?: number;
    handleSetTpPrice: (price: string) => void;
    handleSetSlPrice: (price: string) => void;
    setStopLossPercent: (val: number) => void;
    setTakeProfitPercent: (val: number) => void;
    stopLossPercent?: number | string;
    takeProfitPercent?: number | string;
};

export function TpSlPanel(props: TpSlPanelProps) {
    const {
        isOpen,
        step,
        adjustPrice,
        errorTP,
        errorSl,
        handleClear,
        stopLossPrice,
        takeProfitPrice,
        handleSetTpPrice,
        handleSetSlPrice,
        setStopLossPercent,
        setTakeProfitPercent,
        stopLossPercent,
        takeProfitPercent,
    } = props;

    const { t } = useTranslation();

    const [tpDisplay, setTpDisplay] = useState(() =>
        formatAmountDisplay(String(takeProfitPrice ?? ''))
    );
    const [slDisplay, setSlDisplay] = useState(() =>
        formatAmountDisplay(String(stopLossPrice ?? ''))
    );
    const [tpPercentDisplay, setTpPercentDisplay] = useState(() =>
        String(takeProfitPercent ?? '')
    );
    const [slPercentDisplay, setSlPercentDisplay] = useState(() =>
        String(stopLossPercent ?? '')
    );
    const isTpInternal = useRef(false);
    const isSlInternal = useRef(false);
    const isTpPercentInternal = useRef(false);
    const isSlPercentInternal = useRef(false);

    useEffect(() => {
        if (!isTpInternal.current) {
            setTpDisplay(formatAmountDisplay(String(takeProfitPrice ?? '')));
        }
        isTpInternal.current = false;
    }, [takeProfitPrice]);

    useEffect(() => {
        if (!isSlInternal.current) {
            setSlDisplay(formatAmountDisplay(String(stopLossPrice ?? '')));
        }
        isSlInternal.current = false;
    }, [stopLossPrice]);

    useEffect(() => {
        if (!isTpPercentInternal.current) {
            setTpPercentDisplay(String(takeProfitPercent ?? ''));
        }
        isTpPercentInternal.current = false;
    }, [takeProfitPercent]);

    useEffect(() => {
        if (!isSlPercentInternal.current) {
            setSlPercentDisplay(String(stopLossPercent ?? ''));
        }
        isSlPercentInternal.current = false;
    }, [stopLossPercent]);

    return (
        <AnimatePresence initial={false}>
            {isOpen && (
                <motion.div
                    key="tpsl"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25, ease: 'easeInOut' }}
                    style={{ overflow: 'hidden' }}
                >
                    <div className="space-y-3 pt-1 px-0.5">
                        <div className="space-y-1.5">
                            <div className="flex justify-between items-end px-1">
                                <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">
                                    {t('Take Profit')}
                                </label>
                                <button
                                    onClick={() => handleClear('tp')}
                                    className="text-[10px] font-bold text-ios-blue uppercase tracking-wide"
                                >
                                    {t('Clear')}
                                </button>
                            </div>
                            <div className="flex gap-2">
                                <div
                                    className={clsx(
                                        'w-[60%] bg-surface-light rounded-xl flex items-center px-1 h-12',
                                        {
                                            'outline-1 outline-red-500':
                                                errorTP && takeProfitPrice,
                                        }
                                    )}
                                >
                                    <button
                                        onClick={() => {
                                            const raw = adjustPrice(
                                                takeProfitPrice,
                                                -step
                                            );
                                            isTpInternal.current = true;
                                            handleSetTpPrice(raw);
                                            setTpDisplay(
                                                formatAmountDisplay(raw)
                                            );
                                        }}
                                        className="size-10 flex items-center justify-center text-slate-400 hover:text-slate-600 active:scale-90 transition-transform"
                                    >
                                        <RemoveIcon className="w-5 h-5 cursor-pointer" />
                                    </button>
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        autoComplete="off"
                                        value={tpDisplay}
                                        onChange={(e) => {
                                            const raw = e.target.value.replace(
                                                /,/g,
                                                ''
                                            );
                                            isTpInternal.current = true;
                                            setTpDisplay(e.target.value);
                                            handleSetTpPrice(raw);
                                        }}
                                        className="w-full bg-transparent border-none text-center text-base font-bold text-ios-green placeholder-slate-300 focus:ring-0 p-0 tabular-nums"
                                        placeholder={t('Price')}
                                    />
                                    <button
                                        onClick={() => {
                                            const raw = adjustPrice(
                                                takeProfitPrice,
                                                step
                                            );
                                            isTpInternal.current = true;
                                            handleSetTpPrice(raw);
                                            setTpDisplay(
                                                formatAmountDisplay(raw)
                                            );
                                        }}
                                        className="size-10 flex items-center justify-center text-slate-400 hover:text-slate-600 active:scale-90 transition-transform"
                                    >
                                        <AddIcon className="w-5 h-5 cursor-pointer" />
                                    </button>
                                </div>
                                <div className="w-[40%] flex items-center gap-1 bg-surface-light rounded-xl h-12 px-3">
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        autoComplete="off"
                                        value={tpPercentDisplay}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            isTpPercentInternal.current = true;
                                            setTpPercentDisplay(val);
                                            if (val === '') {
                                                handleClear('tp');
                                            } else {
                                                const num = Number(val);
                                                if (!isNaN(num)) {
                                                    setTakeProfitPercent(num);
                                                }
                                            }
                                        }}
                                        className="w-[calc(100%_-_36px)] bg-transparent border-none text-left text-base font-bold text-ios-green placeholder-slate-300 focus:ring-0 p-0 tabular-nums"
                                        placeholder="0"
                                    />
                                    <span className="text-sm font-bold text-slate-500">
                                        %
                                    </span>
                                </div>
                            </div>
                            <div className="min-h-4.5">
                                {errorTP  ? (
                                    <p className="text-[10px] font-medium text-red-500 text-left px-1 pt-0.5">
                                        {errorTP}
                                    </p>
                                ) : (
                                    <Profit />
                                )}
                            </div>
                        </div>

                        <div className="space-y-1.5">
                            <div className="flex justify-between items-end px-1">
                                <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">
                                    {t('Stop Loss')}
                                </label>
                                <button
                                    onClick={() => handleClear('sl')}
                                    className="text-[10px] font-bold text-ios-blue uppercase tracking-wide"
                                >
                                    {t('Clear')}
                                </button>
                            </div>
                            <div className="flex gap-2">
                                <div
                                    className={clsx(
                                        'w-[60%] bg-surface-light rounded-xl flex items-center px-1 h-12',
                                        {
                                            'outline-1 outline-red-500':
                                                errorSl && stopLossPrice,
                                        }
                                    )}
                                >
                                    <button
                                        onClick={() => {
                                            const raw = adjustPrice(
                                                stopLossPrice,
                                                -step
                                            );
                                            isSlInternal.current = true;
                                            handleSetSlPrice(raw);
                                            setSlDisplay(
                                                formatAmountDisplay(raw)
                                            );
                                        }}
                                        className="size-10 flex items-center justify-center text-slate-400 hover:text-slate-600 active:scale-90 transition-transform"
                                    >
                                        <RemoveIcon className="w-5 h-5 cursor-pointer" />
                                    </button>
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        autoComplete="off"
                                        value={slDisplay}
                                        onChange={(e) => {
                                            const raw = e.target.value.replace(
                                                /,/g,
                                                ''
                                            );
                                            isSlInternal.current = true;
                                            setSlDisplay(e.target.value);
                                            handleSetSlPrice(raw);
                                        }}
                                        className="w-full bg-transparent border-none text-center text-base font-bold text-ios-green placeholder-slate-300 focus:ring-0 p-0 tabular-nums"
                                        placeholder={t('Price')}
                                    />
                                    <button
                                        onClick={() => {
                                            const raw = adjustPrice(
                                                stopLossPrice,
                                                step
                                            );
                                            isSlInternal.current = true;
                                            handleSetSlPrice(raw);
                                            setSlDisplay(
                                                formatAmountDisplay(raw)
                                            );
                                        }}
                                        className="size-10 flex items-center justify-center text-slate-400 hover:text-slate-600 active:scale-90 transition-transform"
                                    >
                                        <AddIcon className="w-5 h-5 cursor-pointer" />
                                    </button>
                                </div>
                                <div className="w-[40%] flex items-center gap-1 bg-surface-light rounded-xl h-12 px-3">
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        autoComplete="off"
                                        value={slPercentDisplay}
                                        onChange={(e) => {
                                            const temp = e.target.value;
                                            const val =
                                                temp.length == 1 &&
                                                temp.charAt(0) !== '.' &&
                                                !temp.startsWith('-')
                                                    ? `-${temp}`
                                                    : temp;
                                            isSlPercentInternal.current = true;
                                            setSlPercentDisplay(val);
                                            if (val === '') {
                                                handleClear('sl');
                                            } else {
                                                const num = Number(val);
                                                if (!isNaN(num)) {
                                                    setStopLossPercent(num);
                                                }
                                            }
                                        }}
                                        className="w-[calc(100%_-_36px)] bg-transparent border-none text-left text-base font-bold placeholder-slate-300 focus:ring-0 p-0 tabular-nums"
                                        placeholder="0"
                                    />
                                    <span className="text-sm font-bold text-slate-500">
                                        %
                                    </span>
                                </div>
                            </div>
                            <div className="min-h-4.5">
                                {errorSl && stopLossPrice ? (
                                    <p className="text-[10px] font-medium text-red-500 text-left px-1 pt-0.5">
                                        {errorSl}
                                    </p>
                                ) : (
                                    <Loss />
                                )}
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
